import ApiClient from "src/utils/ApiClient";
import __ from "src/utils/Translations";
import { getErrorMessage } from "src/utils/constants/apiErrors";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { AccountOwnerOperation, CyclicChargeCandidate } from "./rechargePlanningSystem.models";

const SERVICE_NAME = "recharge-planning-system";

export const RechargePlanningSystemApiClient = new ApiClient({
  baseURL: "/api/recharge-planning-system/api/rest",
}).client;

RechargePlanningSystemApiClient.interceptors.response.use(undefined, (err) => {
  try {
    const errors = err.response?.data?.errors;
    if (errors?.length) {
      const mapped = errors
        .map(({ code, details }: { code: string; details: object }) => getErrorMessage(SERVICE_NAME, code, details)?.errorMessage)
        .filter(Boolean);
      if (mapped.length) {
        return Promise.reject(new Error(__(mapped.join("; "))));
      }
    }
  } catch (e) {
    newRelicErrorReport(e, "rechargePlanningSystem/clients.ts - interceptor");
  }
  return Promise.reject(err);
});

export const fetchEmployeeOperations = async (
  accountOwnerId: string,
  signal?: AbortSignal
): Promise<AccountOwnerOperation[]> => {
  const { data } = await RechargePlanningSystemApiClient.get<AccountOwnerOperation[]>(
    `/v1/private/account-owner/${accountOwnerId}`,
    { signal }
  );
  return data as AccountOwnerOperation[];
};

export const deleteIndividualCharge = async (
  chargeDefinitionId: string,
  accountOwnerId: string,
  pointsBankId: string
): Promise<CyclicChargeCandidate | null> => {
  const { data } = await RechargePlanningSystemApiClient.post(
    `/v2/charges/cyclic/${chargeDefinitionId}/remove-individual-charges`,
    { accountOwnerId, pointsBankId }
  );
  return toNullableCandidate(data);
};

export const setIndividualCharge = async (
  chargeDefinitionId: string,
  accountOwnerId: string,
  pointsBankId: string,
  amount: number
): Promise<CyclicChargeCandidate | null> => {
  const { data } = await RechargePlanningSystemApiClient.post(
    `/v2/charges/cyclic/${chargeDefinitionId}/add-individual-charges`,
    { amount, accountOwnerId, pointsBankId }
  );
  return toNullableCandidate(data);
};

const toNullableCandidate = (data: unknown): CyclicChargeCandidate | null => {
  if (!data || typeof data !== "object") return null;
  if (Object.values(data).every((v) => v == null)) return null;
  return data as CyclicChargeCandidate;
};
